import client from './client';

/**
 * Chat API functions
 * Handles sessions, messages, favorites, and search
 */
export const chatAPI = {
  /**
   * Get all chat sessions for a user
   * @param {string} userId - User ID
   * @param {string} source - Filter by source ("chat" or "study")
   * @returns {Promise<Array>} Array of session objects
   */
  getSessions: async (userId, source = null) => {
    const params = source ? { source } : {};
    const response = await client.get(`/sessions/${userId}`, { params });
    return response.data;
  },
  
  /**
   * Create new chat session
   * @param {string} userId - User ID
   * @param {string} title - Session title
   * @param {string} source - Session source ("chat" or "study")
   * @returns {Promise<Object>} Created session object
   */
  createSession: async (userId, title = 'Yeni Sohbet', source = 'chat') => {
    const response = await client.post('/sessions', null, {
      params: { user_id: userId, title, source }
    });
    return response.data;
  },
  
  /**
   * Delete chat session
   * @param {string} sessionId - Session ID to delete
   */
  deleteSession: async (sessionId) => {
    await client.delete(`/sessions/${sessionId}`);
  },
  
  /**
   * Rename chat session
   * @param {string} sessionId - Session ID
   * @param {string} title - New title
   * @returns {Promise<Object>} Updated session object
   */
  renameSession: async (sessionId, title) => {
    const response = await client.put(`/sessions/${sessionId}`, { title });
    return response.data;
  },
  
  /**
   * Get all messages in a session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Array>} Array of message objects
   */
  getMessages: async (sessionId) => {
    const response = await client.get(`/messages/${sessionId}`);
    return response.data;
  },
  
  /**
   * Send message and get streaming response
   * @param {string} userId - User ID
   * @param {string} sessionId - Session ID
   * @param {string} message - Message content
   * @param {string} model - AI model to use
   * @returns {Promise<ReadableStream>} Stream of response chunks
   */
  sendMessage: async (userId, sessionId, message, model = 'tc-milli-gpt') => {
    // Use fetch for streaming responses instead of axios
    const response = await fetch(`${client.defaults.baseURL}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      },
      body: JSON.stringify({
        user_id: userId,
        session_id: sessionId,
        message,
        model
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.body;
  },
  
  /**
   * Send message with image attachment
   * @param {string} userId - User ID
   * @param {string} sessionId - Session ID
   * @param {string} message - Message content
   * @param {File} imageFile - Image file to attach
   * @param {string} model - AI model to use
   * @returns {Promise<Object>} Response with message and analysis
   */
  sendImageMessage: async (userId, sessionId, message, imageFile, model) => {
    const formData = new FormData();
    formData.append('file', imageFile);
    
    const response = await client.post('/chat/image-analyze', formData, {
      params: {
        user_id: userId,
        session_id: sessionId,
        message,
        model
      },
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  },
  
  /**
   * Search messages across all sessions
   * @param {string} userId - User ID
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of matching messages
   */
  searchMessages: async (userId, query) => {
    const response = await client.get(`/search/${userId}`, {
      params: { query }
    });
    return response.data;
  },
  
  /**
   * Get user's favorite messages
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of favorite objects
   */
  getFavorites: async (userId) => {
    const response = await client.get(`/favorites/${userId}`);
    return response.data;
  },
  
  /**
   * Add message to favorites
   * @param {string} userId - User ID
   * @param {string} messageId - Message ID
   * @param {string} messageContent - Message content
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Created favorite object
   */
  addFavorite: async (userId, messageId, messageContent, sessionId) => {
    const response = await client.post('/favorites', null, {
      params: {
        user_id: userId,
        message_id: messageId,
        message_content: messageContent,
        session_id: sessionId
      }
    });
    return response.data;
  },
  
  /**
   * Remove message from favorites
   * @param {string} favoriteId - Favorite ID to remove
   */
  removeFavorite: async (favoriteId) => {
    await client.delete(`/favorites/${favoriteId}`);
  },
  
  /**
   * Give feedback on a message
   * @param {string} messageId - Message ID
   * @param {string} userId - User ID
   * @param {string} sessionId - Session ID
   * @param {string} rating - Rating ('positive' or 'negative')
   */
  giveFeedback: async (messageId, userId, sessionId, rating) => {
    await client.post('/feedback/rate', {
      message_id: messageId,
      user_id: userId,
      session_id: sessionId,
      rating
    });
  }
};
